import { memo, useMemo } from 'react';
import {
  BackgroundTokens,
  BorderGradientTokens,
  BorderWidthTokens,
  Box,
  Text,
} from '@bonanza-social/moonlight';
import { useSelector } from '@bonanza-social/toolkit/state';
import { selectRegistrationConfig } from '../../state';
import { RegistrationStep } from '../../state/registrationConfig/registrationConfig.types';
import { BreakpointProp } from '@bonanza-social/moonlight/lib/types/styles';

function RegistrationStepTracker({ step }: { step: RegistrationStep }) {
  const { steps = [] } = useSelector(selectRegistrationConfig) ?? { steps: [] };
  const visibleSteps = useMemo(
    () => steps.filter((s) => !s.disableHeader),
    [steps],
  );

  if (!visibleSteps.length) return null;

  const stepId = step.id;
  let currentIndex = stepId
    ? visibleSteps.findIndex((s) => s.id === stepId)
    : 0;
  if (currentIndex < 0) currentIndex = 0;

  return (
    <Box
      width={{ mobile: '95%', tablet: '95%', desktop: 760 }}
      alignSelf="center"
      paddingHorizontal="space-400"
      marginTop="space-400"
      marginBottom="space-200"
      flexDirection="row"
      alignItems="center"
    >
      {visibleSteps.map((stp, index) => {
        const isCurrent = index === currentIndex;
        const isCompleted = index < currentIndex;

        const dotStyle = isCurrent
          ? {
              bg: 'darkSurfaceAccentSolid',
              brd: 'darkSurfaceAccentSolid',
              bw: 'border-width-025',
            }
          : isCompleted
            ? {
                bg: 'darkSurfacePrimary',
                brd: 'darkSurfacePrimary',
                bw: 'border-width-025',
              }
            : {
                bg: 'transparent',
                brd: 'darkSurfacePrimary',
                bw: 'border-width-050',
              };

        return (
          <Box
            key={stp.id}
            flexDirection="row"
            alignItems="center"
            flex={index < visibleSteps.length - 1 ? 1 : undefined}
          >
            <Box alignItems="center" minWidth={40}>
              <Box
                width={16}
                height={16}
                borderRadius="radius-full"
                backgroundColor={
                  dotStyle.bg as BreakpointProp<BackgroundTokens>
                }
                borderColor={
                  dotStyle.brd as BreakpointProp<BorderGradientTokens>
                }
                borderWidth={dotStyle.bw as BreakpointProp<BorderWidthTokens>}
              />
              <Text
                marginTop="space-050"
                variant={isCurrent ? 'body-m-semi' : 'body-m'}
                color={
                  isCurrent ? 'darkSurfaceAccentSolid' : 'darkSurfacePrimary'
                }
              >
                {stp.id.charAt(0).toUpperCase() + stp.id.slice(1)}
              </Text>
            </Box>

            {index < visibleSteps.length - 1 && (
              <Box
                flex={1}
                alignSelf="flex-start"
                marginTop="space-200"
                borderColor="borderSecondary"
                borderTopWidth="border-width-025"
                marginHorizontal="space-100"
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default memo(RegistrationStepTracker);
